import { useState } from 'react'
import type { PRSheetData } from '../types/salesforce'
import type { LanguageSkill, FixedSkillRating } from '../components/SkillsEditor/types'

/**
 * PRSheetエディタの状態管理フック
 *
 * 責務:
 * - PRSheetデータの状態管理
 * - 固定スキルと言語スキルの状態管理
 * - 削除されたIDリストの追跡（スキル、経験、ポートフォリオ等）
 */
export function useEditorState(initialData: PRSheetData | null) {
  const [data, setData] = useState<PRSheetData | null>(initialData)
  const [fixedSkillRatings, setFixedSkillRatings] = useState<Record<string, FixedSkillRating>>({})
  const [languageSkills, setLanguageSkills] = useState<LanguageSkill[]>([])

  // 削除されたIDリスト
  const [deletedSkillIds, setDeletedSkillIds] = useState<string[]>([])
  const [deletedExperienceIds, setDeletedExperienceIds] = useState<string[]>([])
  const [deletedPortfolioItemIds, setDeletedPortfolioItemIds] = useState<string[]>([])
  const [deletedHighlightEpisodeIds, setDeletedHighlightEpisodeIds] = useState<string[]>([])
  const [deletedAwardIds, setDeletedAwardIds] = useState<string[]>([])
  const [deletedEducationIds, setDeletedEducationIds] = useState<string[]>([])
  const [deletedLinkIds, setDeletedLinkIds] = useState<string[]>([])

  // NOTE: 固定スキルと言語スキルの抽出はEditorPageのloadData()内で明示的に行う
  // （useEffect内で行うと、非同期でstate更新が発生し、無限ループの原因になる）

  // データ変更時に削除されたIDを追跡
  const handleDataChange = (newData: PRSheetData, onAutoSave?: () => void) => {
    if (data) {
      // 新規オブジェクトの削除検出
      const deletedExperiences = data.experiences
        .filter((exp) => exp.Id && !newData.experiences.find((e) => e.Id === exp.Id))
        .map((exp) => exp.Id!)
      if (deletedExperiences.length > 0) {
        setDeletedExperienceIds((prev) => [...prev, ...deletedExperiences])
      }

      const deletedPortfolioItems = data.portfolioItems
        .filter((item) => item.Id && !newData.portfolioItems.find((i) => i.Id === item.Id))
        .map((item) => item.Id!)
      if (deletedPortfolioItems.length > 0) {
        setDeletedPortfolioItemIds((prev) => [...prev, ...deletedPortfolioItems])
      }

      const deletedEpisodes = data.highlightEpisodes
        .filter((ep) => ep.Id && !newData.highlightEpisodes.find((e) => e.Id === ep.Id))
        .map((ep) => ep.Id!)
      if (deletedEpisodes.length > 0) {
        setDeletedHighlightEpisodeIds((prev) => [...prev, ...deletedEpisodes])
      }

      const deletedAwards = data.awards
        .filter((award) => award.Id && !newData.awards.find((a) => a.Id === award.Id))
        .map((award) => award.Id!)
      if (deletedAwards.length > 0) {
        setDeletedAwardIds((prev) => [...prev, ...deletedAwards])
      }

      const deletedEdus = data.educations
        .filter((edu) => edu.Id && !newData.educations.find((e) => e.Id === edu.Id))
        .map((edu) => edu.Id!)
      if (deletedEdus.length > 0) {
        setDeletedEducationIds((prev) => [...prev, ...deletedEdus])
      }

      const deletedLnks = data.links
        .filter((link) => link.Id && !newData.links.find((l) => l.Id === link.Id))
        .map((link) => link.Id!)
      if (deletedLnks.length > 0) {
        setDeletedLinkIds((prev) => [...prev, ...deletedLnks])
      }
    }

    setData(newData)

    // 自動保存をスケジュール
    if (onAutoSave) {
      onAutoSave()
    }
  }

  // 削除IDリストをクリア
  const clearDeletedIds = () => {
    setDeletedSkillIds([])
    setDeletedExperienceIds([])
    setDeletedPortfolioItemIds([])
    setDeletedHighlightEpisodeIds([])
    setDeletedAwardIds([])
    setDeletedEducationIds([])
    setDeletedLinkIds([])
  }

  return {
    data,
    setData,
    fixedSkillRatings,
    setFixedSkillRatings,
    languageSkills,
    setLanguageSkills,
    deletedSkillIds,
    setDeletedSkillIds,
    deletedExperienceIds,
    deletedPortfolioItemIds,
    deletedHighlightEpisodeIds,
    deletedAwardIds,
    deletedEducationIds,
    deletedLinkIds,
    handleDataChange,
    clearDeletedIds,
  }
}
